import { Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { LaunchContextService } from '@cargos/sprintpay-services';
import { NgxPermissionsService } from 'ngx-permissions';
import { catchError, distinctUntilChanged, filter, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { LoginService } from './pages/auth/services/login.service';
import { InitialConfigService } from './services/initial-configuration';
import { MaintenanceCopy, MaintenanceService } from './services/maintenance.service';
import { SprintbotService } from './services/sprintbot.service';
import { CustomerService } from './services/utils/user/customer-handler.service';
import { UserSessionService } from './services/utils/user/user-session.service';
import { permissionsName } from './utils/constants';
import { ProfileTypes } from './utils/profile-types';

const MAINTENANCE_BODY_CLASS = 'is-maintenance-mode';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly unsubscribe$: Subject<void>;
    private readonly unsubscribeInit$: Subject<void>;
    private readonly redirectToPortalRoutes = [
        '/login',
        '/password',
        '/signup',
        '/signup-guest',
        '/activate',
        '/activate-guest',
    ];

    private readonly customerService = inject(CustomerService);
    private readonly ngxPermissionsService = inject(NgxPermissionsService);
    private readonly router = inject(Router);
    private readonly loginService = inject(LoginService);
    private readonly userSessionService = inject(UserSessionService);
    private readonly ngZone = inject(NgZone);
    private readonly initialConfigurationService = inject(InitialConfigService);
    private readonly sprintbotService = inject(SprintbotService);
    private readonly launchContextService = inject(LaunchContextService, { optional: true });
    private readonly maintenanceService = inject(MaintenanceService);

    public readonly maintenance$: Observable<boolean> = this.maintenanceService.maintenance$;
    public readonly maintenanceCopy$: Observable<MaintenanceCopy> = this.maintenanceService.maintenanceCopy$;

    constructor() {
        this.unsubscribe$ = new Subject<void>();
        this.unsubscribeInit$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.watchMaintenanceState();
        this.launchContextService?.initializeFromUrl();
        this.subscribeRouterEventsForPermissionsWorkaround();
        this.setInitialConfiguration();
        this.checkSessionTokenChange();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
        this.unsubscribe$.complete();
        this.unsubscribeInit$.next();
        this.unsubscribeInit$.complete();
    }

    unsubscribe(): void {
        this.unsubscribe$.next();
    }

    // TODO: Refactor this implementation to address the issue when a customer switches from authenticated to guest mode.
    // The current approach opens a new channel with new observables each time the profile changes,
    // which causes problems. Closing the observable is not a viable solution as it may lead to issues
    // when the token expires and the customer attempts to log in again.
    setInitialConfiguration(): void {
        this.userSessionService.onChangeProfile$().subscribe({
            next: (onChangeProfile) => {
                if (onChangeProfile) {
                    this.unsubscribe();
                    return this.setInitialConfig();
                }
            },
        });
    }

    private setInitialConfig(): void {
        this.initialConfigurationService
            .getCustomerNotifications()
            .pipe(
                take(1),
                catchError(() => of('Complete'))
            )
            .subscribe();

        // TODO: Implement a mechanism to display an error or block the system if this fails - medium
        this.initialConfigurationService.setRolesAndPermissions().pipe(takeUntil(this.unsubscribe$)).subscribe();

        // TODO: We shouldn't need to get the payment method events if the customer doesn't supports it
        this.initialConfigurationService
            .getEventsPaymentMethods()
            .pipe(
                catchError(() => of('Complete')),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // TODO: We shouldn't need to get the payment method events if the customer doesn't supports it
        this.userSessionService
            .hasTokenAndIsNotExpired$()
            .pipe(
                filter((hasTokenAndIsNotExpired) => hasTokenAndIsNotExpired),
                distinctUntilChanged(),
                switchMap(() => this.initialConfigurationService.getPaymentMethodsAndSetByDefaultIfNeeded()),
                catchError(() => of('Complete')),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        // TODO: We shouldn't need to get the payment methods if the customer doesn't supports it
        this.userSessionService
            .hasTokenAndIsNotExpired$()
            .pipe(
                filter((hasTokenAndIsNotExpired) => hasTokenAndIsNotExpired),
                distinctUntilChanged(),
                switchMap(() => this.initialConfigurationService.getPaymentMethodSelectedAndGetCart$()),
                catchError(() => of('Complete')),
                takeUntil(this.unsubscribe$)
            )
            .subscribe();

        this.userSessionService
            .isAuthenticatedAndIsAValidToken$()
            .pipe(
                filter((authenticated) => authenticated),
                switchMap(() => this.initialConfigurationService.getPaymentMethodsOnCartAboveThreshold()),
                catchError(() => of('Complete')),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(); // TODO: We shouldn't need to get the payment methods if the customer doesn't supports it
    }

    checkSessionTokenChange(): void {
        this.userSessionService
            .hasToken$()
            .pipe(
                filter((token) => token),
                switchMap(() => this.userSessionService.isAuthenticated$()),
                filter((authenticated) => authenticated),
                takeUntil(this.unsubscribeInit$)
            )
            .subscribe({
                next: () => {
                    this.redirectToAdminPortal();
                },
            });
    }

    private redirectToAdminPortal(): void {
        const route = `/${this.router.url.split('/')[1]}`;
        if (!this.redirectToPortalRoutes.includes(route)) return;
        const customer = this.customerService.getCustomer();
        const customerProfile =
            customer?.profileType && customer.profileType !== ProfileTypes.FORWARDER_FACILITY
                ? customer.profileType
                : ProfileTypes.FORWARDER;
        this.customerService.setActiveProfileView(customerProfile);
        const url = this.loginService.getRedirectLink();
        this.ngZone.run(() => this.router.navigate([url]));
    }

    private subscribeRouterEventsForPermissionsWorkaround(): void {
        this.router.events.pipe(takeUntil(this.unsubscribeInit$)).subscribe((event) => {
            if (
                event instanceof NavigationStart &&
                event.url.includes('admin/facilityPayments/newPayment?') &&
                !event.url.includes('searchTerms=')
            ) {
                this.ngxPermissionsService.addPermission(permissionsName.guest);
            }
        });
    }

    /**
     * Reacts to the Statsig `maintenance_mode` gate.
     *
     * - Toggles a body class so global CSS can hide side effects that live
     *   outside `<app-root>` (e.g. the Sprintbot chat widget attached to
     *   `document.body`) while the overlay is up.
     * - Only starts the chatbot once Statsig has *confirmed* the gate is OFF,
     *   preventing the widget from ever booting under maintenance.
     */
    private watchMaintenanceState(): void {
        this.maintenanceService.maintenance$
            .pipe(distinctUntilChanged(), takeUntil(this.unsubscribeInit$))
            .subscribe((isMaintenance) => {
                document.body.classList.toggle(MAINTENANCE_BODY_CLASS, isMaintenance);
            });

        this.maintenanceService.maintenanceReady$
            .pipe(
                filter((isMaintenance) => !isMaintenance),
                takeUntil(this.unsubscribeInit$)
            )
            .subscribe(() => this.sprintbotService.initialize());
    }
}
